import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  finalizeLessonVideo,
  requestMuxIngest,
  type MuxIngestStatus,
} from '@/services/creatorApi';

/**
 * useVideoEditOps — run the video edit pipeline (trim, audio enhance,
 * color grade, background replace) on a video that is ALREADY uploaded to S3.
 *
 * Unlike the old forced post-record wizard this hook absorbed, there is no
 * fresh recording (File) upload step; the caller hands us an existing
 * { s3Key, url }, so no File is ever involved. Every backend endpoint below
 * accepts a URL/key input, so nothing needs a File.
 *
 * Ops chain a working pointer ({ currentKey, currentUrl }) held in a ref so that
 * `await trim(); await enhanceAudio();` reads the freshly-produced clip rather
 * than a stale render-closure value. The original input is never mutated —
 * revert() restores it, and finalize() only copies the working clip into a
 * durable creator-products/ key when at least one op was applied.
 */

// ---- Public types ---------------------------------------------------------

export interface VideoEditInput {
  s3Key: string;
  url: string;
}

/** The chained working clip after each mutating op. */
export interface VideoEditWorkingRef {
  currentKey: string;
  currentUrl: string;
}

export type AudioEnhanceMode = 'light' | 'natural' | 'strong';

export interface ColorAdjust {
  brightness: number;
  contrast: number;
  saturation: number;
  temperature: number;
  exposure: number;
}

/** A preset key (string) or a custom slider adjustment object. */
export type ColorInput = string | ColorAdjust;

export interface ColorPreview {
  key: string;
  label: string;
  preview: string;
}

export interface ReplaceBackgroundOpts {
  /** 'green-screen' keys out to a green matte; 'backdrop' composites over a studio image. */
  mode: 'green-screen' | 'backdrop';
  /** stock-backgrounds/<backdrop>.png — required when mode === 'backdrop'. */
  backdrop?: string;
  /** Composite clip length in seconds (Shotstack needs it up front). Defaults to 60. */
  durationSec?: number;
  /** Render the composite 9:16 so a phone-shot clip isn't letterboxed. */
  portrait?: boolean;
}

/** The exact patch shape lessons/episodes consume after an edit is applied. */
export interface FinalizedVideoPatch {
  s3Key: string;
  fileType: 'video';
  muxAssetId?: string;
  muxPlaybackId?: string;
  muxIngestStatus?: MuxIngestStatus;
}

export type VideoEditOp = 'trim' | 'enhanceAudio' | 'color' | 'background' | 'finalize';

// ---- Error contract -------------------------------------------------------

export interface InsufficientCreditsError {
  code: 'INSUFFICIENT_CREDITS';
  required?: number;
  available?: number;
}
export interface PlusRequiredError {
  code: 'PLUS_REQUIRED';
}
export interface OpFailedError {
  code: 'OP_FAILED';
  message: string;
}
export type VideoEditError = InsufficientCreditsError | PlusRequiredError | OpFailedError;

export type OpResult<T> = { ok: true; data: T } | { ok: false; error: VideoEditError };

export interface VideoEditState {
  currentUrl: string;
  currentKey: string;
  busy: boolean;
  activeOp: VideoEditOp | null;
  error: VideoEditError | null;
  appliedOps: VideoEditOp[];
}

export interface UseVideoEditOps extends VideoEditState {
  trim: (startSec: number, endSec: number) => Promise<OpResult<VideoEditWorkingRef>>;
  enhanceAudio: (mode?: AudioEnhanceMode) => Promise<OpResult<VideoEditWorkingRef>>;
  colorPreviews: () => Promise<OpResult<ColorPreview[]>>;
  applyColor: (presetOrAdjustments: ColorInput) => Promise<OpResult<VideoEditWorkingRef>>;
  replaceBackground: (opts: ReplaceBackgroundOpts) => Promise<OpResult<VideoEditWorkingRef>>;
  finalize: () => Promise<OpResult<FinalizedVideoPatch>>;
  revert: () => void;
}

// ---- Internals ------------------------------------------------------------

// Typed carrier so a rejected fetch/poll surfaces the right upsell (402/403).
class VideoEditOpException extends Error {
  constructor(public opError: VideoEditError) {
    super(opError.code === 'OP_FAILED' ? opError.message : opError.code);
    this.name = 'VideoEditOpException';
  }
}

const opFailed = (message: string) => new VideoEditOpException({ code: 'OP_FAILED', message });

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// The trim tool defaults to the rotator bucket; our clip may live in
// hey-joe-videos (durable creator-products/). Detect which so "specified key
// does not exist" can't happen.
const bucketOf = (url: string) =>
  url.includes('/hey-joe-video-rotator/') || url.includes('hey-joe-video-rotator.s3')
    ? 'hey-joe-video-rotator'
    : 'hey-joe-videos';

const keyFromUrl = (url: string) =>
  url
    .replace(/^\/api\/s3\//, '')
    .replace(/^hey-joe-video-rotator\//, '')
    .replace(/^hey-joe-videos\//, '');

export function useVideoEditOps(input: VideoEditInput): UseVideoEditOps {
  const { getAuthHeaders } = useAuth();

  // Live working pointer (ref) so sequential awaited ops chain correctly; the
  // state mirror is only for rendering.
  const originalRef = useRef<VideoEditWorkingRef>({ currentKey: input.s3Key, currentUrl: input.url });
  const workingRef = useRef<VideoEditWorkingRef>({ currentKey: input.s3Key, currentUrl: input.url });
  const appliedRef = useRef<VideoEditOp[]>([]);
  const runningRef = useRef(false);

  const [current, setCurrent] = useState<VideoEditWorkingRef>(workingRef.current);
  const [appliedOps, setAppliedOps] = useState<VideoEditOp[]>([]);
  const [busy, setBusy] = useState(false);
  const [activeOp, setActiveOp] = useState<VideoEditOp | null>(null);
  const [error, setError] = useState<VideoEditError | null>(null);

  // Re-init when the caller points the hook at a different source video.
  useEffect(() => {
    const fresh: VideoEditWorkingRef = { currentKey: input.s3Key, currentUrl: input.url };
    originalRef.current = { ...fresh };
    workingRef.current = { ...fresh };
    appliedRef.current = [];
    runningRef.current = false;
    setCurrent(fresh);
    setAppliedOps([]);
    setBusy(false);
    setActiveOp(null);
    setError(null);
  }, [input.s3Key, input.url]);

  const setWorking = useCallback((op: VideoEditOp, next: VideoEditWorkingRef): VideoEditWorkingRef => {
    workingRef.current = next;
    setCurrent(next);
    if (!appliedRef.current.includes(op)) {
      appliedRef.current = [...appliedRef.current, op];
      setAppliedOps([...appliedRef.current]);
    }
    return next;
  }, []);

  const postJson = useCallback(
    async (path: string, body: unknown): Promise<any> => {
      const res = await fetch(path, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (res.status === 402) {
        const e = await res.json().catch(() => ({}));
        throw new VideoEditOpException({ code: 'INSUFFICIENT_CREDITS', required: e.required, available: e.available });
      }
      if (res.status === 403) {
        throw new VideoEditOpException({ code: 'PLUS_REQUIRED' });
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw opFailed(e.error || e.details || 'Could not start this step');
      }
      return res.json();
    },
    [getAuthHeaders]
  );

  const pollJob = useCallback(
    async (statusUrl: string, interval = 3000, timeoutMs = 30 * 60 * 1000): Promise<any> => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        await sleep(interval);
        const res = await fetch(statusUrl, { headers: getAuthHeaders() });
        const data = await res.json().catch(() => ({ status: 'failed', error: 'bad status' }));
        if (data.status === 'completed' || data.status === 'done') return data;
        if (data.status === 'failed' || data.status === 'not_found') {
          throw opFailed(data.error || 'Processing failed');
        }
      }
      throw opFailed('Timed out');
    },
    [getAuthHeaders]
  );

  // Wraps a mutating op: single-flight guard + busy/activeOp/error bookkeeping +
  // turning thrown VideoEditOpExceptions into a typed OpResult.
  const runOp = useCallback(
    async <T>(op: VideoEditOp, fn: () => Promise<T>): Promise<OpResult<T>> => {
      if (runningRef.current) {
        return { ok: false, error: { code: 'OP_FAILED', message: 'Another operation is already running' } };
      }
      runningRef.current = true;
      setBusy(true);
      setActiveOp(op);
      setError(null);
      try {
        const data = await fn();
        return { ok: true, data };
      } catch (err) {
        const opError: VideoEditError =
          err instanceof VideoEditOpException
            ? err.opError
            : { code: 'OP_FAILED', message: (err as Error)?.message || 'Operation failed' };
        setError(opError);
        return { ok: false, error: opError };
      } finally {
        runningRef.current = false;
        setBusy(false);
        setActiveOp(null);
      }
    },
    []
  );

  const trim = useCallback(
    (startSec: number, endSec: number) =>
      runOp('trim', async () => {
        const { currentKey, currentUrl } = workingRef.current;
        if (!endSec || endSec <= startSec) throw opFailed('Trim end must be after the start');
        const videoKey = currentKey || keyFromUrl(currentUrl);
        const j = await postJson('/api/split-video', {
          videoKey,
          sourceBucket: bucketOf(currentUrl),
          trimStart: startSec,
          splitPoints: [{ time: endSec, label: 'lesson' }],
          trimOnly: true,
          async: true,
        });
        const done = await pollJob(`/api/split-video/status/${j.jobId}`);
        const seg = (done.segments || [])[0] || {};
        const k = seg.segmentKey || seg.s3Key;
        const url = seg.downloadUrl || (k ? `/api/s3/${bucketOf(currentUrl)}/${k}` : '');
        if (!url) throw opFailed('No trimmed segment returned');
        return setWorking('trim', { currentKey: k || currentKey, currentUrl: url });
      }),
    [runOp, postJson, pollJob, setWorking]
  );

  const enhanceAudio = useCallback(
    (mode: AudioEnhanceMode = 'natural') =>
      runOp('enhanceAudio', async () => {
        const { currentUrl } = workingRef.current;
        const j = await postJson('/api/enhance-video-audio', { videoUrl: currentUrl, mode });
        const done = await pollJob(`/api/enhance-video-audio/status/${j.jobId}`, 5000);
        const url = done.videoUrl || (done.s3Key ? `/api/s3/${done.s3Key}` : '');
        if (!url) throw opFailed('No enhanced audio returned');
        return setWorking('enhanceAudio', { currentKey: done.s3Key || '', currentUrl: url });
      }),
    [runOp, postJson, pollJob, setWorking]
  );

  const colorPreviews = useCallback(async (): Promise<OpResult<ColorPreview[]>> => {
    try {
      const res = await fetch('/api/filter/previews', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ videoUrl: workingRef.current.currentUrl }),
      });
      if (res.status === 402) {
        const e = await res.json().catch(() => ({}));
        return { ok: false, error: { code: 'INSUFFICIENT_CREDITS', required: e.required, available: e.available } };
      }
      if (res.status === 403) return { ok: false, error: { code: 'PLUS_REQUIRED' } };
      if (!res.ok) return { ok: false, error: { code: 'OP_FAILED', message: 'Could not load previews' } };
      const d = await res.json();
      const previews: ColorPreview[] = (d.previews || []).filter((p: ColorPreview) => p.key !== 'none');
      return { ok: true, data: previews };
    } catch (err) {
      return { ok: false, error: { code: 'OP_FAILED', message: (err as Error)?.message || 'Could not load previews' } };
    }
  }, [getAuthHeaders]);

  const applyColor = useCallback(
    (presetOrAdjustments: ColorInput) =>
      runOp('color', async () => {
        const { currentUrl } = workingRef.current;
        const isPreset = typeof presetOrAdjustments === 'string';
        const filter = isPreset ? presetOrAdjustments : 'none';
        const colorAdjustments = isPreset ? undefined : presetOrAdjustments;
        if (filter === 'none' && !colorAdjustments) throw opFailed('Pick a grade or adjust the sliders');
        const j = await postJson('/api/filter/apply', { videoUrl: currentUrl, filter, colorAdjustments });
        const done = await pollJob(`/api/filter/apply/status/${j.jobId}`);
        const url = done.downloadUrl || (done.key ? `/api/s3/hey-joe-video-rotator/${done.key}` : '');
        if (!url) throw opFailed('No graded clip returned');
        return setWorking('color', { currentKey: done.key || '', currentUrl: url });
      }),
    [runOp, postJson, pollJob, setWorking]
  );

  const replaceBackground = useCallback(
    (opts: ReplaceBackgroundOpts) =>
      runOp('background', async () => {
        const { currentUrl } = workingRef.current;
        const origin = typeof window !== 'undefined' ? window.location.origin : '';

        if (opts.mode === 'green-screen') {
          const j = await postJson('/api/remove-video-background', { videoUrl: currentUrl, outputType: 'green-screen' });
          const done = await pollJob(`/api/remove-video-background/status/${j.jobId}`, 5000, 60 * 60 * 1000);
          const url = done.videoUrl || (done.s3Key ? `/api/s3/${done.s3Key}` : '');
          if (!url) throw opFailed('No keyed clip returned');
          return setWorking('background', { currentKey: done.s3Key || '', currentUrl: url });
        }

        if (!opts.backdrop) throw opFailed('Pick a backdrop');
        // Matte the subject, then composite over the chosen studio backdrop via Shotstack.
        const j = await postJson('/api/remove-video-background', { videoUrl: currentUrl, outputType: 'foreground-mask' });
        const done = await pollJob(`/api/remove-video-background/status/${j.jobId}`, 5000, 60 * 60 * 1000);
        const maskUrl = origin + (done.videoUrl || `/api/s3/${done.s3Key}`);
        const srcUrl = origin + (done.normalizedSourceUrl || currentUrl);
        const backdropUrl = `${origin}/stock-backgrounds/${opts.backdrop}.png`;
        const len = opts.durationSec || 60;
        const json = {
          timeline: {
            background: '#000000',
            tracks: [
              {
                clips: [
                  { asset: { type: 'luma', src: maskUrl }, start: 0, length: len },
                  { asset: { type: 'image', src: backdropUrl }, start: 0, length: len, fit: 'cover' },
                ],
              },
              { clips: [{ asset: { type: 'video', src: srcUrl, volume: 1 }, start: 0, length: len }] },
            ],
          },
          // Portrait sources render 9:16 so a phone-shot clip isn't letterboxed.
          output: { format: 'mp4', resolution: 'hd', ...(opts.portrait ? { aspectRatio: '9:16' } : {}) },
        };
        const rj = await postJson('/api/shotstack/render', { json });
        const rendered = await pollJob(`/api/shotstack/render-status/${rj.id}`, 4000, 30 * 60 * 1000);
        // Shotstack returns a CDN url; finalize() copies it into a durable key later.
        const url = rendered.videoUrl || rendered.url;
        if (!url) throw opFailed('No composited clip returned');
        return setWorking('background', { currentKey: '', currentUrl: url });
      }),
    [runOp, postJson, pollJob, setWorking]
  );

  const finalize = useCallback(
    () =>
      runOp('finalize', async () => {
        const { currentKey, currentUrl } = workingRef.current;
        let key = originalRef.current.currentKey;
        if (appliedRef.current.length) {
          // Copy the working clip into a durable creator-products/ key. If that
          // fails, fall back to the last working key rather than the original.
          try {
            key = (await finalizeLessonVideo(currentUrl)).key;
          } catch {
            key = currentKey || key;
          }
        }
        const mux = await requestMuxIngest({ s3Key: key, kind: 'paid' }).catch(() => null);
        const patch: FinalizedVideoPatch = {
          s3Key: key,
          fileType: 'video',
          muxAssetId: mux?.muxAssetId,
          muxPlaybackId: mux?.muxPlaybackId || undefined,
          muxIngestStatus: mux?.muxIngestStatus,
        };
        return patch;
      }),
    [runOp]
  );

  const revert = useCallback(() => {
    if (runningRef.current) return;
    const orig = { ...originalRef.current };
    workingRef.current = orig;
    appliedRef.current = [];
    setCurrent(orig);
    setAppliedOps([]);
    setError(null);
  }, []);

  return {
    currentUrl: current.currentUrl,
    currentKey: current.currentKey,
    busy,
    activeOp,
    error,
    appliedOps,
    trim,
    enhanceAudio,
    colorPreviews,
    applyColor,
    replaceBackground,
    finalize,
    revert,
  };
}

export default useVideoEditOps;
